import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Col, Form, Input, InputNumber, Row, Space, Table, Tag, Typography, Drawer, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import reportsApi, { CustomerConsistencyQuery, CustomerConsistencyResponse, CustomerConsistencyRow } from '../../services/reportsApi';

const { Title, Text } = Typography;

const dbOrder = ['V3_StDeri', 'V3_StDeriLTD', 'V3_SerkanCanta', 'V3_SerkanCantaLTD'];

type TableRow = CustomerConsistencyRow & { key: string };

const CustomerConsistency: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CustomerConsistencyResponse | null>(null);
  const [onlyMismatch, setOnlyMismatch] = useState(true);
  const [details, setDetails] = useState<CustomerConsistencyRow | null>(null);

  const columns: ColumnsType<TableRow> = useMemo(() => [
    {
      title: 'Müşteri Kodu',
      dataIndex: 'customerCode',
      key: 'customerCode',
      fixed: 'left',
      width: 160,
    },
    {
      title: 'Müşteri Adı',
      key: 'customerName',
      width: 220,
      render: (_value: unknown, record: TableRow) => {
        // V3_StDeri öncelikli, yoksa ilk bulunan isim
        const preferred = record.values?.[dbOrder[0]]?.customerName
          ?? dbOrder.map(db => record.values?.[db]?.customerName).find(v => !!v)
          ?? '';
        return <Text>{preferred}</Text>;
      }
    },
    ...dbOrder.map(db => ({
      title: db,
      key: `presence_${db}`,
      width: 140,
      render: (_value: unknown, record: TableRow) => (
        record.presence?.[db]
          ? <Tag color="green">Var</Tag>
          : <Tag color="red">Yok</Tag>
      )
    })),
    {
      title: 'Ad Aynı',
      key: 'nameEqual',
      width: 90,
      render: (_value: unknown, r: TableRow) => r.equalFlags?.CustomerName_AllEqual ? <Tag color="green">Evet</Tag> : <Tag color="orange">Hayır</Tag>
    },
    {
      title: 'Vergi/Döviz/VIP',
      key: 'mixEqual',
      width: 140,
      render: (_value: unknown, r: TableRow) => {
        const ok = (r.equalFlags?.CurrencyCode_AllEqual ?? 0) && (r.equalFlags?.IsVIP_AllEqual ?? 0) && (r.equalFlags?.TaxNumber_AllEqual ?? 0);
        return ok ? <Tag color="green">Hepsi Aynı</Tag> : <Tag color="orange">Fark Var</Tag>;
      }
    },
    {
      title: 'Detay',
      key: 'detail',
      fixed: 'right',
      width: 90,
      render: (_value: unknown, r: TableRow) => <Button size="small" onClick={() => setDetails(r)}>Gör</Button>
    }
  ], []);

  const tableData: TableRow[] = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows
      // Sadece eksik olanları listele: herhangi bir DB'de presence=false
      .filter(r => !onlyMismatch || Object.values(r.presence || {}).some(v => !v))
      .map(r => ({ ...r, key: r.customerCode }));
  }, [data, onlyMismatch]);

  const onSearch = async () => {
    try {
      // Debug: form submit başladı
      // eslint-disable-next-line no-console
      console.log('[CustomerConsistency] onSearch START');
      const values = await form.validateFields();
      const payload: CustomerConsistencyQuery = {
        codeLike: values.codeLike ?? '%%',
        limit: values.limit ?? 200,
        offset: 0,
        includeEqualSummary: false,
      };
      // eslint-disable-next-line no-console
      console.log('[CustomerConsistency] Payload:', payload);
      setLoading(true);
      const res = await reportsApi.getCustomerConsistency(payload);
      // eslint-disable-next-line no-console
      console.log('[CustomerConsistency] API response:', res);
      setData(res);
      message.success('Rapor verileri alındı');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Analiz isteği hatası:', err);
      message.error('Rapor alınırken bir hata oluştu. Detay için konsolu kontrol edin.');
    } finally {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.log('[CustomerConsistency] onSearch END');
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Title level={3}>Müşteri Tutarlılık Analizi</Title>
      <Form form={form} layout="vertical" onFinish={onSearch} initialValues={{ codeLike: '%%', limit: 200 }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item name="codeLike" label="Müşteri Kodu Filtre">
              <Input placeholder="Örn: A% veya %%" allowClear />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item name="limit" label="Limit">
              <InputNumber min={1} max={5000} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Checkbox checked={onlyMismatch} onChange={e => setOnlyMismatch(e.target.checked)}>Sadece Eksikler</Checkbox>
          </Col>
          <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <Space>
              <Button onClick={() => form.resetFields()}>Temizle</Button>
              <Button type="primary" htmlType="submit" loading={loading}>Ara</Button>
            </Space>
          </Col>
        </Row>
      </Form>

      <Table
        loading={loading}
        dataSource={tableData}
        columns={columns}
        size="small"
        bordered
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
      />

      <Drawer
        width={600}
        title={`Detay - ${details?.customerCode ?? ''}`}
        open={!!details}
        onClose={() => setDetails(null)}
      >
        {details && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {dbOrder.map(db => (
              <div key={db}>
                <Title level={5} style={{ marginBottom: 8 }}>{db} {details.presence?.[db] ? <Tag color="green">Var</Tag> : <Tag color="red">Yok</Tag>}</Title>
                {details.values?.[db] ? (
                  <div style={{ paddingLeft: 8 }}>
                    {Object.entries(details.values[db]!).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', gap: 8 }}>
                        <Text type="secondary" style={{ minWidth: 180 }}>{k}</Text>
                        <Text>{String(v ?? '')}</Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary">Kayıt yok</Text>
                )}
              </div>
            ))}
          </Space>
        )}
      </Drawer>
    </Space>
  );
};

export default CustomerConsistency;
